'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useLayoutEffect, useRef } from 'react';
import { cn } from '@utils/cn';
import { getPrimaryBottomTabs, isRouteItemActive } from '@utils/route-utils';
import { usePageChrome } from '@components/layout/page-chrome-context';

interface BottomTabBarProps {
  organization_id: string;
}

/**
 * Floating iOS-style tab island: 4 labeled primary destinations. Rendered only below `desktop-chrome`.
 */
export function BottomTabBar({ organization_id }: BottomTabBarProps) {
  const t = useTranslations();
  const pathname = usePathname();
  const { chrome } = usePageChrome();
  const tabs = getPrimaryBottomTabs(organization_id, t);
  const measureRef = useRef<HTMLDivElement>(null);

  // Measure expanded geometry at the narrowest dock width. This reserves enough
  // space for either state, even with wrapped labels, without changing the page's
  // scroll height during the size transition.
  useLayoutEffect(() => {
    const dock = measureRef.current;
    if (!dock) return;
    const root = document.documentElement;
    const measure = () => {
      const height = dock.getBoundingClientRect().height;
      if (height > 0) {
        root.style.setProperty('--mobile-dock-measured-height', `${height}px`);
      }
    };
    measure();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    observer?.observe(dock);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--mobile-dock-measured-height');
    };
  }, [chrome?.hideMobileTabs]);

  if (tabs.length < 4 && process.env.NODE_ENV === 'development') {
    console.warn('BottomTabBar: expected 4 primary tabs but got', tabs.length);
  }

  if (chrome?.hideMobileTabs) return null;

  const renderTabs = (measurement = false) =>
    tabs.map(tab => {
      const active = isRouteItemActive(pathname, tab);
      const className = cn(
        'mobile-dock-tab inline-flex min-w-11 flex-1 flex-col items-center justify-center gap-0.5 rounded-selected px-1 py-1 font-medium',
        active ? 'bg-nav-selected text-brand-text' : 'text-secondary-foreground'
      );
      const content = (
        <>
          {tab.icon ? (
            <tab.icon className="mobile-dock-icon shrink-0" aria-hidden />
          ) : null}
          <span className="max-w-full text-center leading-tight break-words">
            {tab.label}
          </span>
        </>
      );
      return measurement ? (
        <div key={tab.id} className={className}>
          {content}
        </div>
      ) : (
        <Link
          key={tab.id}
          href={tab.href}
          aria-label={tab.label}
          aria-current={active ? 'page' : undefined}
          className={cn(className, 'active:scale-[0.97]')}
        >
          {content}
        </Link>
      );
    });

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 px-5 pb-[var(--mobile-tab-island-offset)] desktop-chrome:hidden">
      <div className="relative mx-auto">
        <div
          ref={measureRef}
          data-mobile-dock-measure=""
          aria-hidden
          inert
          className="mobile-dock mobile-dock-measure flex items-stretch rounded-dock"
        >
          {renderTabs(true)}
        </div>
        <nav
          aria-label={t('app-chrome.primary-navigation')}
          className="mobile-dock chrome-glass pointer-events-auto mx-auto flex items-stretch rounded-dock"
        >
          {renderTabs()}
        </nav>
      </div>
    </div>
  );
}
